// W15-2 — THE NAME LAUNDER. RAW VENDOR AND DATABASE TOKENS NEVER REACH A MEMBER.
//
// **HER CAPTURE WAS `Mean_Node`**, printed to a paying member in the astrocartography room's "what
// moves at {place}" list. It is not a typo and it is not rare: it is what the vendor calls that
// point, rendered straight through, and it is in the SAVED payload of every astrocartography report
// this site has ever written (measured — the distinct planet tokens in `reports.data.houseChanges`
// are Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto, Chiron, Mean_Node).
//
// **HER RULING ON THE TERM: `Mean_Node` AND `True_Node` BOTH RENDER AS "North Node".** The mean/true
// distinction is an astronomer's choice of ephemeris, not something a member asked for or can act
// on, and printing it hands her a decision she never made.
//
// This module imports NOTHING, so BOTH the server and the client read the one table and a `.mjs`
// gate can RUN it. That is the entry requirement every shared law on this site is held to.

// ---------------------------------------------------------------------------
// THE TABLE. Vendor token -> what a member is told it is. OWNER-EXTENSIBLE: a new point is one row.
//
// Keys are matched case-insensitively and underscore/space-insensitively (see `displayPoint`), so
// `Mean_Node`, `mean node` and `MEAN_NODE` all resolve without three rows.
// ---------------------------------------------------------------------------
export const POINT_NAMES = {
  sun: "Sun",
  moon: "Moon",
  mercury: "Mercury",
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
  uranus: "Uranus",
  neptune: "Neptune",
  pluto: "Pluto",
  chiron: "Chiron",

  // HER RULING: both nodes are the North Node to a member.
  meannode: "North Node",
  truenode: "North Node",
  northnode: "North Node",
  node: "North Node",
  southnode: "South Node",
  meansouthnode: "South Node",
  truesouthnode: "South Node",

  meanlilith: "Lilith",
  truelilith: "Lilith",
  lilith: "Lilith",
  blackmoonlilith: "Lilith",

  // Angles. The vendor sends abbreviations; a member reads the words.
  ascendant: "Ascendant",
  asc: "Ascendant",
  ac: "Ascendant",
  descendant: "Descendant",
  dsc: "Descendant",
  midheaven: "Midheaven",
  mc: "Midheaven",
  medthecoeli: "Midheaven",
  imumcoeli: "Imum Coeli",
  ic: "Imum Coeli",

  partoffortune: "Part of Fortune",
  fortune: "Part of Fortune",
  vertex: "Vertex",
  earth: "Earth",
};

// The lookup key: case-folded, and with every separator a vendor might use removed. This is what
// lets one row answer `Mean_Node`, `mean node`, `MEAN-NODE` and `meanNode`.
export function pointKey(token) {
  return String(token ?? "").trim().toLowerCase().replace(/[\s_\-.]/g, "");
}

// **THE RESOLVER, AND ITS DEFAULT IS NOT SILENCE.** R17's lesson: a receiving side whose default is
// a plausible-looking passthrough is how a raw token reaches a member with nothing thrown and
// nothing logged. An unmapped token is still LAUNDERED (underscores never survive, whatever else
// happens) and `isMapped` tells a caller — and the gate — that this was a guess.
export function displayPoint(token) {
  const key = pointKey(token);
  if (!key) return "";
  const mapped = POINT_NAMES[key];
  if (mapped) return mapped;
  // Unmapped: strip the separators and title-case the words, so the WORST case a member can see is
  // "Mean Node" and never `Mean_Node`. The gate below is what makes the miss visible to us.
  return String(token).trim().replace(/[_\-.]+/g, " ").replace(/\s+/g, " ")
    .split(" ").filter(Boolean)
    .map((w) => (w === w.toUpperCase() && w.length <= 3 ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ");
}

export function isMappedPoint(token) {
  return Object.prototype.hasOwnProperty.call(POINT_NAMES, pointKey(token));
}

// Every token the vendor is known to send, so a gate can walk the roster rather than spot-check it.
export const KNOWN_VENDOR_TOKENS = [
  "Sun", "Moon", "Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto",
  "Chiron", "Mean_Node", "True_Node", "Mean_Lilith", "True_Lilith", "Ascendant", "Midheaven",
  "MC", "AC", "Part_of_Fortune", "Vertex",
];
